import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostBinding,
  computed,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ENVIRONMENT_TOKEN, ResetPasswordResult } from '@aksia/infrastructure';
import { ToasterService } from '@aksia/ui';
import { map, interval, take, Subscription } from 'rxjs';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';

type Outcome = 'form' | 'success' | 'expired' | 'failed';

/** Seconds the success screen waits before returning the user to the application. */
const REDIRECT_DELAY_SECONDS = 5;

@Component({
  selector: 'app-reset-password',
  imports: [],
  templateUrl: './reset-password.content.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordPageContent implements OnInit {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly httpClient = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly environment = inject(ENVIRONMENT_TOKEN);
  private readonly toaster = inject(ToasterService);
  protected readonly currentYear = new Date().getFullYear();

  protected readonly newPassword = signal('');
  protected readonly confirmPassword = signal('');
  protected readonly newPasswordVisible = signal(false);
  protected readonly confirmPasswordVisible = signal(false);
  protected readonly inFlight = signal(false);
  protected readonly attemptedSubmit = signal(false);
  protected readonly formShaking = signal(false);
  readonly outcome = signal<Outcome>('form');

  ngOnInit(): void {
    if (!this.paramsValid()) return;
    this.validateToken();
  }

  /** Server-supplied explanation for the `failed` outcome. */
  protected readonly failureMessage = signal(
    'Your password could not be reset due to an internal error. Please contact your Administrator.',
  );

  /** Where to send the user after a successful reset; absent when the link carried no client. */
  protected readonly redirectUrl = signal<string | undefined>(undefined);
  protected readonly secondsUntilRedirect = signal(REDIRECT_DELAY_SECONDS);
  private redirectCountdown?: Subscription;

  protected readonly email = toSignal(
    this.route.queryParamMap.pipe(map((p) => p.get('email') ?? undefined)),
    { initialValue: undefined as string | undefined },
  );

  protected readonly token = toSignal(
    this.route.queryParamMap.pipe(map((p) => p.get('token') ?? undefined)),
    { initialValue: undefined as string | undefined },
  );

  /**
   * The OAuth client the reset was requested from, carried by the emailed link. Absent when
   * the reset started outside an authorization request.
   */
  protected readonly client = toSignal(
    this.route.queryParamMap.pipe(map((p) => p.get('client') ?? undefined)),
    { initialValue: undefined as string | undefined },
  );

  protected readonly paramsValid = computed(
    () => !!this.email() && !!this.token(),
  );

  protected readonly policyRules = computed(() => {
    const p = this.newPassword();
    return [
      { label: 'At least 8 characters', met: p.length >= 8 },
      { label: 'One digit (0–9)', met: /\d/.test(p) },
      { label: 'One lowercase letter (a–z)', met: /[a-z]/.test(p) },
      { label: 'One uppercase letter (A–Z)', met: /[A-Z]/.test(p) },
      { label: 'One special character', met: /[^a-zA-Z0-9]/.test(p) },
    ];
  });

  protected readonly policyMet = computed(() =>
    this.policyRules().every((r) => r.met),
  );

  protected readonly mismatchError = computed(
    () =>
      this.attemptedSubmit() &&
      !!this.confirmPassword() &&
      this.newPassword() !== this.confirmPassword(),
  );

  @HostBinding('class')
  protected readonly hostClass = 'auth-page';

  private readonly formUrlEncodedHeaders = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded',
  });

  protected updateNewPassword(value: string) {
    this.newPassword.set(value);
  }

  private triggerShake() {
    this.formShaking.set(true);
    setTimeout(() => this.formShaking.set(false), 400);
  }

  protected showNewPasswordError() {
    return this.attemptedSubmit() && !this.policyMet();
  }

  protected submit(event?: Event) {
    event?.preventDefault();
    this.attemptedSubmit.set(true);

    if (!this.policyMet() || this.newPassword() !== this.confirmPassword()) {
      this.triggerShake();
      return;
    }

    this.inFlight.set(true);
    const url = `${this.environment.microservices_api_url}/account/reset-password`;
    const client = this.client();
    let body = new HttpParams()
      .set('email', this.email()!)
      .set('token', this.token()!)
      .set('newPassword', this.newPassword());

    if (client) body = body.set('client', client);

    this.httpClient
      .post<ResetPasswordResult>(url, body.toString(), {
        headers: this.formUrlEncodedHeaders,
      })
      .subscribe({
        next: (result) => {
          this.inFlight.set(false);
          this.outcome.set('success');
          this.startRedirect(result?.returnUrl);
        },
        error: (err: HttpErrorResponse) => {
          this.inFlight.set(false);
          if (err.status === 429) {
            this.toaster.error(
              'Too many requests. Please wait a few minutes and try again.',
            );
            return;
          }
          switch (err.error?.error) {
            case 'invalid_token':
              this.outcome.set('expired');
              break;
            case 'invalid_password':
              this.toaster.error(
                err.error.error_description ??
                  'The password does not meet the requirements.',
              );
              break;
            case 'password_reset_failed':
              // The server rolled the password back, so nothing changed and the link still
              // works — a retry is worth offering once the downstream system recovers.
              if (err.error?.error_description)
                this.failureMessage.set(err.error?.error_description);
              this.outcome.set('failed');
              break;
            default: {
              const correlationId = err.error?.correlation_id as
                string | undefined;
              this.toaster.error(
                correlationId
                  ? `Something went wrong. Please try again. Reference: ${correlationId}`
                  : 'Something went wrong. Please try again.',
              );
            }
          }
        },
      });
  }

  /**
   * Counts down to the application the reset was requested from. Does nothing when there is
   * no return url — the success screen then keeps its "reopen the application" copy.
   */
  private startRedirect(returnUrl?: string) {
    if (!returnUrl) return;

    this.redirectUrl.set(returnUrl);
    this.secondsUntilRedirect.set(REDIRECT_DELAY_SECONDS);

    this.redirectCountdown = interval(1000)
      .pipe(take(REDIRECT_DELAY_SECONDS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const remaining = this.secondsUntilRedirect() - 1;
        this.secondsUntilRedirect.set(remaining);

        if (remaining <= 0) this.redirectNow();
      });
  }

  protected redirectNow() {
    const target = this.redirectUrl();
    if (!target) return;

    this.clearRedirectTimer();

    // Already an absolute url to the application's own entry point, so unlike the login
    // page's relative returnUrl it is never prefixed with the API base.
    document.location.href = target;
  }

  private clearRedirectTimer() {
    this.redirectCountdown?.unsubscribe();
    this.redirectCountdown = undefined;
  }

  /** Returns to the form after a failed reset, keeping what the user already typed. */
  protected retryReset() {
    this.attemptedSubmit.set(false);
    this.outcome.set('form');
  }

  protected goToForgotPassword() {
    this.router.navigate(['/forgot-password']);
  }

  private validateToken() {
    this.inFlight.set(true);
    const url = `${this.environment.microservices_api_url}/account/validate-token/reset-password`;
    const client = this.client();
    let body = new HttpParams()
      .set('email', this.email()!)
      .set('token', this.token()!);

    if (client) body = body.set('client', client);

    this.httpClient
      .post<ResetPasswordResult>(url, body.toString(), {
        headers: this.formUrlEncodedHeaders,
      })
      .subscribe({
        next: (result) => {
          this.inFlight.set(false);
        },
        error: (err: HttpErrorResponse) => {
          this.inFlight.set(false);
          if (err.status === 429) {
            this.toaster.error(
              'Too many requests. Please wait a few minutes and try again.',
            );
            return;
          }

          if (err.error?.error == 'invalid_token') this.outcome.set('expired');
          else if (err.error?.error == 'invalid_request')
            this.toaster.error(
              err.error.error_description ?? 'Invalid request.',
            );
          else {
            const correlationId = err.error?.correlation_id as
              string | undefined;
            this.toaster.error(
              correlationId
                ? `Something went wrong. Please try again. Reference: ${correlationId}`
                : 'Something went wrong. Please try again.',
            );
          }
        },
      });
  }
}
